/**
 * Hidden tax (iter310) — display only.
 *
 * Tax is ALWAYS calculated at the site jurisdiction, stored, and pushed to
 * Foundation as tax. When a document hides tax, the customer's copy shows
 * no tax line and the tax dollars ride inside the Material figures
 * instead — pro-rata by material dollars, with the rounding remainder on
 * the largest material line so the printed total matches the internal
 * total to the cent. Labor, Freight, Drive Time, Equipment and Disposal
 * are never touched.
 */

export const HIDE_TAX_ROLES = ["service_manager", "account_manager", "project_manager", "admin"];

export const canToggleHideTax = (role) =>
  HIDE_TAX_ROLES.includes(String(role || "").toLowerCase());

export const NO_MATERIAL_MSG =
  "This document hides tax but has no material line to carry it. Add a material line, or show tax on this document.";

/** Split `amount` across `weights` pro-rata in whole cents; the largest
 *  weight absorbs the remainder so the parts sum back to `amount`. */
export const distributeByWeight = (weights, amount) => {
  const ws = (weights || []).map(w => Math.max(0, Number(w) || 0));
  const cents = Math.round((Number(amount) || 0) * 100);
  const total = ws.reduce((s, w) => s + w, 0);
  if (!ws.length || !cents || total <= 0) return ws.map(() => 0);
  const parts = ws.map(w => Math.floor((w / total) * cents));
  const remainder = cents - parts.reduce((s, p) => s + p, 0);
  if (remainder) parts[ws.indexOf(Math.max(...ws))] += remainder;
  return parts.map(p => p / 100);
};

/** Fold a hidden tax amount into enriched proposal/CO display lines.
 *  Mutates the given lines (they are already per-render copies).
 *  Returns false when there is no material to carry the tax. */
export const foldHiddenTaxIntoLines = (lines, taxAmount, materialKey = "_displayMaterial", totalKey = "_displayTotal") => {
  const rows = lines || [];
  if (!taxAmount || !rows.length) return true;
  const weights = rows.map(l => Math.max(0, Number(l[materialKey]) || 0));
  if (weights.reduce((s, w) => s + w, 0) <= 0) return false;
  distributeByWeight(weights, taxAmount).forEach((add, i) => {
    if (!add) return;
    rows[i][materialKey] = Number(((Number(rows[i][materialKey]) || 0) + add).toFixed(2));
    rows[i][totalKey] = Number(((Number(rows[i][totalKey]) || 0) + add).toFixed(2));
  });
  return true;
};

/** Same, across every scope of a proposal (document-wide pro-rata). */
export const foldHiddenTaxIntoScopes = (ccScopes, taxAmount) => {
  const all = (ccScopes || []).flatMap(sc => sc._lines || []);
  return foldHiddenTaxIntoLines(all, taxAmount);
};

/** Fold into flat invoice / WO / CO line rows. `isMaterial` decides which
 *  rows may carry tax; `amountKeys` are the money fields to bump. */
export const foldHiddenTaxIntoRows = (rows, taxAmount, {
  isMaterial = (r) => String(r.cost_type || "").toLowerCase() === "material" || String(r.cost_type || "") === "2",
  materialKey = null,
  amountKeys = ["total"],
} = {}) => {
  const out = (rows || []).map(r => ({ ...r }));
  if (!taxAmount || !out.length) return { rows: out, ok: true };
  const weights = out.map(r => {
    if (materialKey) return Math.max(0, Number(r[materialKey]) || 0);
    if (!isMaterial(r)) return 0;
    return Math.max(0, Number(r.total) || (Number(r.quantity) || 0) * (Number(r.unit_price) || 0));
  });
  if (weights.reduce((s, w) => s + w, 0) <= 0) return { rows: out, ok: false };
  distributeByWeight(weights, taxAmount).forEach((add, i) => {
    if (!add) return;
    if (materialKey) out[i][materialKey] = Number(((Number(out[i][materialKey]) || 0) + add).toFixed(2));
    amountKeys.forEach(k => { out[i][k] = Number(((Number(out[i][k]) || 0) + add).toFixed(2)); });
    const qty = Number(out[i].quantity) || 0;
    if (qty && out[i].unit_price != null && amountKeys.includes("total")) {
      out[i].unit_price = Number((Number(out[i].total) / qty).toFixed(4));
    }
  });
  return { rows: out, ok: true };
};
